import {
  Body1,
  Card,
  Divider,
  InfoLabel,
  Link,
  makeStyles,
  shorthands,
  Spinner,
  Text,
  Title1,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowSync24Regular,
  ChevronRight24Regular,
  CloudSync24Regular,
  HeartPulse24Regular,
  Mail24Regular,
  PeopleTeam24Regular,
  Settings24Regular,
} from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import packageInfo from "../../package.json";
import { MessageBar, MessageBarBody } from "../components/Common/ScrollableMessageBar";
import { TERMS_AND_CONDITIONS_URL } from "../config/externalURLs";
import useEventTracker from "../hooks/useEventTracker";
import { useGeneralConfig } from "../hooks/useGeneralConfig";
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { VersionInfo, versionService } from "../services/versionService";
import { ElementButton, PageName } from "../tracking/tracking.types";

const useStyles = makeStyles({
  settings: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("24px"),
    paddingBottom: "100px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.borderBottom("3px", "solid", "#0047AB"),
    ...shorthands.padding("0", "0", "20px", "0"),
    ...shorthands.margin("0", "0", "24px", "0"),
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    width: "100%",
    backgroundColor: "#ffffff",
    padding: "25px",
    color: "#5b5fc7",
    textAlign: "center",
    zIndex: 999,
  },
  footerLink: {
    color: "#5b5fc7",
    justifyContent: "center",
    marginLeft: "260px",
    textAlign: "center",
  },
  configCard: {
    ...shorthands.padding("24px"),
    ...shorthands.gap("20px"),
    display: "flex",
    flexDirection: "column",
  },
  formGrid: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("20px"),
  },
  actionButtons: {
    display: "flex",
    ...shorthands.gap("12px"),
    ...shorthands.margin("20px", "0", "0", "0"),
  },
  placeholder: {
    ...shorthands.padding("32px"),
  },
  placeholderTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#323130",
    ...shorthands.margin("0", "0", "16px", "0"),
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
  },
  placeholderDescription: {
    fontSize: "16px",
    color: "#605e5c",
    lineHeight: "1.5",
  },
  versionData: {
    display: "flex",
    flexDirection: "row",
    width: "fit-content",
    columnGap: "8px",
    fontSize: "14px",
    marginTop: "-24px",
    padding: "0 4px",
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px')
  }
});

const HIDE_MENUS_FOR_PILOT = true; // We decided to hide some settings for the pilot release

export const Settings: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const eventTracker = useEventTracker();

  const [error] = useScrollableMessage<string | null>(null);
  const [success] = useScrollableMessage<string | null>(null);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const { loadingConfig, loadConfig } = useGeneralConfig();

  useEffect(() => {
    const fetchVersionInfo = async () => {
      const vInfo = await versionService.getVersion();
      setVersionInfo(vInfo);
    };

    fetchVersionInfo();
    loadConfig();
  }, [loadConfig]);

  if (loadingConfig) {
    return (
      <div className={styles.loadingContainer} data-testid="general-config-loading-container-div">
        <Spinner size="large" label="Loading settings..." data-testid="general-config-loading-configuration-spinner" />
      </div>);
    
  }

  return (
    <div className={styles.settings} data-testid="settings-settings-div">

      {/* Header */}
      <div className={styles.header} data-testid="settings-header-div">
        <Title1 data-testid="settings-settings-title1">Settings</Title1>
      </div>

      <div className={styles.versionData}>
        <Body1>
          ADM{" "}
          {versionInfo?.currentVersion?.armVersion ? (
            <>
              <InfoLabel data-testid="version-icon"
                info={
                  <>
                    <div className={styles.sectionTitle} data-testid="arm-version">
                      Version details for ADM v
                      {versionInfo?.currentVersion?.armVersion}{" "}
                    </div>
                    <br />
                    <Body1 data-testid="frontend-version">ADM Frontend v{packageInfo.version}</Body1>
                    <br />
                    <Body1 data-testid="backend-version">
                      ADM Backend{" "}
                      {versionInfo?.currentVersion?.backendVersion
                        ? `v${versionInfo?.currentVersion?.backendVersion}`
                        : "Loading..."}
                    </Body1>
                  </>
                }
              >
                v{versionInfo?.currentVersion?.armVersion}{" "}
              </InfoLabel>
            </>
          ) : (
            "Loading..."
          )}
        </Body1>
        {versionInfo?.updateAvailable && versionInfo?.updateLink && (
          <>
            <Divider vertical />
            <Body1>
              <Link href={versionInfo.updateLink} target="_blank">
                Upgrade to v{versionInfo?.targetVersion?.armVersion}
              </Link>
            </Body1>
          </>
        )}
      </div>

      {/* Messages */}
      {error &&
      <MessageBar intent="error" data-testid="settings-error-message-bar">
          <MessageBarBody data-testid="settings-error-message-bar-body">{error}</MessageBarBody>
        </MessageBar>
      }

      {success &&
      <MessageBar intent="success" data-testid="settings-success-message-bar">
          <MessageBarBody data-testid="settings-success-message-bar-body">{success}</MessageBarBody>
        </MessageBar>
      }

      {/* General Settings */}
      <Card
        className={styles.configCard}
        style={{ cursor: "pointer" }}
        onClick={() => {
          eventTracker.eventTrackOnElementClicked({ pageName: PageName.SETTINGS, elementName: ElementButton.GENERAL_CONFIGURATION, elementType: "menu-item" }).catch(() => {});
          navigate("/general-config");
        }}
         data-testid="settings-general-config-card"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          data-testid="settings-general-config-card-layout"
        >
          <div data-testid="settings-general-config-card-details">
            <Text
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
               data-testid="settings-general-configuration-text"
            >
              <Settings24Regular />
              General Configuration
            </Text>
            <Text
              style={{
                fontSize: "14px",
                color: tokens.colorNeutralForeground2,
              }}
               data-testid="settings-manage-entra-id-applications-permissions-client-text"
            >
              Manage Entra ID applications, permissions, client IDs, and
              Azure/M365 integration settings
            </Text>
          </div>
          <ChevronRight24Regular
            style={{ fontSize: "24px", color: tokens.colorNeutralForeground3 }}
          />
        </div>
      </Card>

      {/* Tenant Configuration */}
      <Card
        className={styles.configCard}
        style={{ cursor: "pointer" }}
        onClick={() => {
          eventTracker.eventTrackOnElementClicked({ pageName: PageName.SETTINGS, elementName: ElementButton.TENANT_CONFIGURATION, elementType: "menu-item" }).catch(() => {});
          navigate("/tenant-config");
        }}
        data-testid="settings-tenant-config-card"
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          data-testid="settings-tenant-config-card-layout"
        >
          <div data-testid="settings-tenant-config-card-details">
            <Text
              style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}
              data-testid="settings-tenant-configuration-text"
            >
              <PeopleTeam24Regular />
              Tenant Configuration
            </Text>
            <Text
              style={{ fontSize: "14px", color: tokens.colorNeutralForeground2 }}
              data-testid="settings-tenant-configuration-subtitle-text"
            >
              Restrict how users can participate in federated group chats
            </Text>
          </div>
          <ChevronRight24Regular style={{ fontSize: "24px", color: tokens.colorNeutralForeground3 }} />
        </div>
      </Card>

      {/* Email Notifications Configuration */}
      <Card
        className={styles.configCard}
        style={{ cursor: "pointer" }}
        onClick={() => {
          eventTracker.eventTrackOnElementClicked({ pageName: PageName.SETTINGS, elementName: ElementButton.EMAIL_NOTIFICATION_SETTINGS, elementType: "menu-item" }).catch(() => {});
          navigate("/email-settings");
        }}
        data-testid="email-settings-card"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
           data-testid="settings-email-config-card-layout"
        >
          <div data-testid="settings-email-config-card-details">
            <Text
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              data-testid="settings-email-notification-settings-text"
            >
              <Mail24Regular />
              Email Notification Settings
            </Text>
            <Text
              style={{
                fontSize: "14px",
                color: tokens.colorNeutralForeground2,
              }}
              data-testid="settings-configure-email-notifications-for-policy-updates-text"
            >
              Configure email notifications for policy updates and system alerts
            </Text>
          </div>
          <ChevronRight24Regular
            style={{ fontSize: "24px", color: tokens.colorNeutralForeground3 }}
          />
        </div>
      </Card>

      {/* Drift Monitoring Settings */}
      {!HIDE_MENUS_FOR_PILOT && (
        <Card
          className={styles.configCard}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/drift-settings")}
          data-testid="settings-drift-config-card"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            data-testid="settings-drift-config-card-layout"
          >
            <div data-testid="settings-drift-config-card-details">
              <Text
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                data-testid="settings-drift-monitoring-configuration-text"
              >
                <ArrowSync24Regular />
                Drift Monitoring Configuration
              </Text>
              <Text
                style={{
                  fontSize: "14px",
                  color: tokens.colorNeutralForeground2,
                }}
                data-testid="settings-configure-drift-detection-intervals-notifications-and-text"
              >
                Configure drift detection intervals, notifications, and
                automatic cleanup settings
              </Text>
            </div>
            <ChevronRight24Regular
              style={{
                fontSize: "24px",
                color: tokens.colorNeutralForeground3,
              }}
            />
          </div>
        </Card>
      )}

      {/* Domain Feed Configuration */}
      <Card
        className={styles.configCard}
        style={{ cursor: "pointer" }}
        onClick={() => {
          eventTracker.eventTrackOnElementClicked({ pageName: PageName.SETTINGS, elementName: ElementButton.DOMAIN_FEED_CONFIGURATION, elementType: "menu-item" }).catch(() => {});
          navigate("/domain-feed-settings");
        }}
        data-testid="settings-domain-feed-card"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          data-testid="settings-domain-feed-card-layout"
        >
          <div data-testid="settings-domain-feed-card-details">
            <Text
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              data-testid="settings-domain-feed-configuration-text"
            >
              <CloudSync24Regular />
              Domain Feed Configuration
            </Text>
            <Text
              style={{
                fontSize: "14px",
                color: tokens.colorNeutralForeground2,
              }}
              data-testid="settings-configure-domain-feed-api-endpoint-authentication-text"
            >
              Configure Domain Feed API endpoint, authentication, and
              synchronization settings
            </Text>
          </div>
          <ChevronRight24Regular
            style={{ fontSize: "24px", color: tokens.colorNeutralForeground3 }}
          />
        </div>
      </Card>

      {/* Teams Sync Configuration */}
      {!HIDE_MENUS_FOR_PILOT && (
        <Card
          className={styles.configCard}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/teams-sync-settings")}
          data-testid="settings-teams-sync-card"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            data-testid="settings-teams-sync-card-layout"
          >
            <div data-testid="settings-teams-sync-card-details">
              <Text
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                data-testid="settings-teams-policy-sync-scheduler-text"
              >
                <ArrowSync24Regular />
                Teams Policy Sync Scheduler
              </Text>
              <Text
                style={{
                  fontSize: "14px",
                  color: tokens.colorNeutralForeground2,
                }}
                data-testid="settings-configure-microsoft-teams-policy-synchronization-intervals-text"
              >
                Configure Microsoft Teams policy synchronization intervals and
                settings
              </Text>
            </div>
            <ChevronRight24Regular
              style={{
                fontSize: "24px",
                color: tokens.colorNeutralForeground3,
              }}
            />
          </div>
        </Card>
      )}

      {/* ADM Security Groups */}
      {!HIDE_MENUS_FOR_PILOT && (
        <Card
          className={styles.configCard}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/security-groups")}
          data-testid="settings-security-groups-card"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            data-testid="settings-security-groups-card-layout"
          >
            <div data-testid="settings-security-groups-card-details">
              <Text
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                data-testid="settings-adm-security-groups-text"
              >
                <PeopleTeam24Regular />
                ADM Security Groups
              </Text>
              <Text
                style={{
                  fontSize: "14px",
                  color: tokens.colorNeutralForeground2,
                }}
                data-testid="settings-view-and-manage-security-groups-created-text"
              >
                View and manage security groups created by the ADM tool for
                policy assignments
              </Text>
            </div>
            <ChevronRight24Regular
              style={{
                fontSize: "24px",
                color: tokens.colorNeutralForeground3,
              }}
            />
          </div>
        </Card>
      )}

      {/* System Status and Health */}
      <Card
        className={styles.configCard}
        style={{ cursor: "pointer" }}
        onClick={() => {
          eventTracker.eventTrackOnElementClicked({ pageName: PageName.SETTINGS, elementName: ElementButton.SYSTEM_STATUS_HEALTH, elementType: "menu-item" }).catch(() => {});
          navigate("/system-status");
        }}
        data-testid="settings-system-status-card"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          data-testid="settings-system-status-card-layout"
        >
          <div data-testid="settings-system-status-card-details">
            <Text
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              data-testid="settings-system-status-health-text"
            >
              <HeartPulse24Regular />
              System Status & Health
            </Text>
            <Text
              style={{
                fontSize: "14px",
                color: tokens.colorNeutralForeground2,
              }}
              data-testid="settings-monitor-database-health-container-resources-memory-text"
            >
              Monitor database health, container resources, memory usage, and
              system performance metrics
            </Text>
          </div>
          <ChevronRight24Regular
            style={{ fontSize: "24px", color: tokens.colorNeutralForeground3 }}
          />
        </div>
      </Card>

      {/*Footer*/}
      <div className={styles.footer} data-testid="settings-footer-div">
        <Text className={styles.footerLink} data-testid="settings-footer-text">
          <a
            href={TERMS_AND_CONDITIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="settings-footer-link"
          >
            Terms and Conditions
          </a>
        </Text>
      </div>
    </div>
  );
};
